import type { HierarchyIssue, HierarchyNode } from '@gestion-permisos/contracts';
import { Link } from 'react-router-dom';
import { BackToAdmin, firstForbidden } from '../admin/admin-ui.js';
import { useHierarchy } from '../admin/people-api.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { Card, CardContent } from '../components/card.js';
import { PageHeader } from '../components/page-header.js';

const ISSUE_LABELS: Record<HierarchyIssue, string> = {
  MANAGER_INACTIVE: 'Responsable inactivo',
  AREA_INACTIVE: 'Área inactiva',
  NO_ACTIVE_ROLE: 'Sin rol vigente',
  INACTIVE_WITH_ACTIVE_SUBORDINATES: 'Inactivo con subordinados activos',
};

/** Etiquetas de cada nodo, con los colores `status-*` de `styles.css` (como las insignias de estado). */
const BADGE = 'rounded-md px-2 py-0.5 text-xs font-medium';

/**
 * Jerarquía (W8): `GET /employees/hierarchy`, en preorden y con `depth`. Cada nodo muestra los problemas
 * **tal como llegan**: el cliente no calcula ciclos ni problemas, y no muestra `areaId` ni `managerId` como
 * nombres. Con el aspecto de las pantallas ya adaptadas: cabecera y el árbol (o el estado vacío) en una
 * tarjeta. El árbol es un esquema anidado, solo para pintarlo: cada persona es una pastilla y sus
 * subordinados cuelgan de una línea vertical (`nest`, a partir del preorden y de `depth`).
 */
export function AdminHierarchyPage() {
  const hierarchy = useHierarchy();
  const denied = firstForbidden(hierarchy.error);
  if (denied) return <ApiErrorView error={denied} />;

  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <BackToAdmin />
      <PageHeader title="Jerarquía" description="Árbol de responsables y problemas señalados." />
      <div>
        {hierarchy.isPending ? <output className="block text-muted-foreground">Cargando…</output> : null}
        {hierarchy.isError ? <ApiErrorView error={hierarchy.error} /> : null}
        {hierarchy.isSuccess && hierarchy.data.length === 0 ? (
          <Card className="items-center py-16 text-center">
            <p className="text-sm text-muted-foreground">No hay empleados.</p>
          </Card>
        ) : null}
        {hierarchy.isSuccess && hierarchy.data.length > 0 ? (
          <Card>
            <CardContent>
              <ul aria-label="Jerarquía" className="space-y-3">
                {nest(hierarchy.data).map((branch) => (
                  <HierarchyBranch key={branch.node.id} branch={branch} />
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

interface Branch {
  node: HierarchyNode;
  children: Branch[];
}

/**
 * Anida el preorden solo para pintarlo, en una pasada y sin mirar `managerId`: un nodo de `depth` N+1 cuelga
 * del último de `depth` N, y al bajar `depth` se cierran las ramas más profundas. Si `depth` salta más de uno,
 * no inventa niveles: el nodo va a la raíz, junto al último de su mismo `depth` (que, sin padre abierto, también
 * saltó y está en la raíz).
 */
function nest(nodes: readonly HierarchyNode[]): Branch[] {
  const roots: Branch[] = [];
  // El último nodo de cada `depth` en la rama abierta.
  const open = new Map<number, Branch>();
  for (const node of nodes) {
    for (const depth of open.keys()) if (depth > node.depth) open.delete(depth);
    const branch: Branch = { node, children: [] };
    (open.get(node.depth - 1)?.children ?? roots).push(branch);
    open.set(node.depth, branch);
  }
  return roots;
}

/** Una persona: la pastilla y, si tiene subordinados, su lista colgando de la línea de la rama. */
function HierarchyBranch({ branch: { node, children } }: { branch: Branch }) {
  const pill = node.depth === 0 ? 'border border-primary/40 bg-primary/5' : 'bg-muted/60 hover:bg-muted';
  return (
    <li data-depth={node.depth}>
      <div className={`inline-flex w-fit max-w-full flex-wrap items-center gap-2 rounded-lg px-3 py-2 ${pill}`}>
        <Link
          to={`/administracion/empleados/${node.id}`}
          className={`font-medium ${node.active ? 'text-primary' : 'text-muted-foreground'} underline-offset-4 hover:underline`}
        >
          {node.fullName}
        </Link>
        {node.isDirectorGeneral ? <span className={`${BADGE} bg-secondary text-secondary-foreground`}>Director General</span> : null}
        {node.active ? null : <span className={`${BADGE} bg-status-cancelled text-status-cancelled-foreground`}>Inactivo</span>}
        {node.issues.map((issue) => (
          <span key={issue} className={`${BADGE} bg-status-pending text-status-pending-foreground`}>
            {ISSUE_LABELS[issue]}
          </span>
        ))}
      </div>
      {children.length > 0 ? (
        <ul className="mt-3 ml-4 space-y-3 border-l-2 border-l-primary/40 pl-4">
          {children.map((child) => (
            <HierarchyBranch key={child.node.id} branch={child} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
